import DepressionContentPage from "@/components/DepressionContentPage";
import type { ContentSection } from "@/components/ContentSections";
import type { FAQItem } from "@/components/DepressionContentPage";
import type { RelatedLink } from "@/components/RelatedPages";

const faqs: FAQItem[] = [
  {
    question: "Is there a TMS clinic in Fort Lauderdale?",
    answer:
      "Neuro Wellness TMS Centers of America does not have a Fort Lauderdale office. The clinic is in Coral Springs, in northern Broward County — about 30 to 40 minutes from downtown Fort Lauderdale by car. Patients from Las Olas, Victoria Park, Wilton Manors, and the beachfront neighborhoods regularly make the drive up I-95 or the Turnpike to reach the clinic.",
  },
  {
    question: "How long is the drive from Fort Lauderdale to the clinic?",
    answer:
      "From downtown Fort Lauderdale to 9734 W Sample Rd in Coral Springs is roughly 30 to 40 minutes in typical traffic via I-95 North to the Sawgrass Expressway, or the Florida Turnpike to Sample Road. Patients in northern Fort Lauderdale — near Tamarac, North Lauderdale, or Imperial Point — are often 15 to 25 minutes away. Rush-hour traffic on I-95 can add 10 to 15 minutes, so off-peak travel is worth planning around.",
  },
  {
    question: "Can I commute from Fort Lauderdale every day for TMS?",
    answer:
      "For standard TMS, yes — a 30 to 40 minute drive each way is manageable for a single daily session over six weeks. For SAINT®, which packs 50 sessions into five consecutive days with roughly 50-minute breaks between sessions, you are at the clinic for most of each day. Some Fort Lauderdale patients commute daily; others stay at a hotel near Coral Springs for the treatment week to avoid twice-daily rush-hour drives and rest between sessions.",
  },
  {
    question: "Does insurance cover TMS for Fort Lauderdale patients?",
    answer:
      "Coverage depends on your insurance plan, not your city. Standard TMS for depression is widely covered by commercial plans and Medicare when criteria are met. SAINT® is billed under Category III CPT codes and coverage varies more. Neuro Wellness verifies your benefits for free before treatment so you understand your cost-sharing, whether your plan is based in Broward County or elsewhere. Benefits verification does not guarantee payment.",
  },
  {
    question: "What treatments are available for Fort Lauderdale patients?",
    answer:
      "Neuro Wellness offers standard TMS, the FDA-cleared SAINT® Neuromodulation System (accelerated, fMRI-guided TMS), SPRAVATO® (esketamine), IV ketamine, Prism for PTSD, psychotherapy, and medication management — all under one roof in Coral Springs. A consultation helps determine which option fits your diagnosis, history, and schedule.",
  },
];

const sections: ContentSection[] = [
  {
    variant: "text",
    heading: "Getting to Neuro Wellness from Fort Lauderdale",
    body: [
      "Neuro Wellness TMS Centers of America is located at 9734 W Sample Rd, 2nd Floor, Suite B, Coral Springs, FL 33065 — in northern Broward County, roughly 30 to 40 minutes from downtown Fort Lauderdale by car. There is no Fort Lauderdale office; patients travel to Coral Springs for every visit.",
      "The most common routes from Fort Lauderdale are I-95 North to the Sawgrass Expressway (FL-869) west, or the Florida Turnpike north to Sample Road. From the Las Olas and downtown corridor, I-95 is usually fastest. From the western suburbs — Plantation, Sunrise, or Davie — the Turnpike or Sawgrass can be more direct. Patients in northern Fort Lauderdale neighborhoods like Tamarac, North Lauderdale, and Imperial Point are often just 15 to 25 minutes away.",
      "Rush-hour traffic on I-95 between 7:00–9:00 AM and 4:30–6:30 PM can add 10 to 15 minutes each way, so many patients schedule sessions mid-morning or early afternoon to avoid the worst of it.",
    ],
  },
  {
    variant: "text",
    heading: "What the treatment schedule means for Fort Lauderdale commuters",
    body: [
      "The schedule you choose shapes the commute. Standard TMS is one session per day, five days a week, for about six weeks — roughly 36 visits. For a Fort Lauderdale patient, that is a 60 to 80 minute round trip each weekday, which most people can fit around work and family.",
      "SAINT® is different: 50 short sessions delivered over five consecutive days, with roughly 50-minute breaks between sessions. You are at the clinic for most of each day. Some Fort Lauderdale patients commute daily; others prefer to stay at a hotel near Coral Springs for the week so they can rest between sessions and skip the rush-hour drive. The concentrated format means one week instead of six, which can suit patients who cannot step away from work for two months.",
      "SPRAVATO® (esketamine) and IV ketamine follow their own schedules — typically a series of sessions over several weeks, each requiring a two-hour observation period. The care team helps you plan the timing that fits your commute from Fort Lauderdale.",
    ],
  },
  {
    variant: "text",
    heading: "Neighborhoods and nearby towns we serve from Fort Lauderdale",
    body: [
      "We regularly see patients from across Fort Lauderdale and the surrounding communities. From the downtown core — Las Olas Isles, Victoria Park, Rio Vista, and Flagler Village — the drive up I-95 is direct. From the beachside neighborhoods — Coral Ridge, Harbor Beach, and the barrier islands — allow a few extra minutes to cross the bridges.",
      "Patients also come from Wilton Manors, Poinsettia Heights, Imperial Point, Harbour Inlet, and Dorsey-Riverbend. North of the city, Tamarac, North Lauderdale, Lauderdale Lakes, Oakland Park, and Margate are especially close to the clinic — often under 25 minutes. West of Fort Lauderdale, patients from Plantation, Sunrise, and Davie reach Coral Springs via the Sawgrass Expressway.",
    ],
  },
  {
    variant: "text",
    heading: "SAINT® vs standard TMS for Fort Lauderdale patients",
    body: [
      "Neuro Wellness offers both, so the choice is clinical, not logistical alone. Standard TMS is one session a day for about six weeks — a manageable daily commute from Fort Lauderdale, and more broadly covered by insurance. SAINT® is an FDA-cleared accelerated protocol that uses an fMRI scan to personalize the stimulation target and delivers all 50 sessions in five days. It requires a concentrated one-week commitment and an MRI before treatment begins.",
      "For a Fort Lauderdale patient weighing the two, the question is often about time: can you take a week off for SAINT®, or is a six-week daily schedule more realistic? A consultation reviews your diagnosis, medication history, and eligibility for each. Read more about how SAINT® compares to standard TMS in our detailed comparison.",
    ],
  },
  {
    variant: "text",
    heading: "Starting with a consultation",
    body: [
      "The first step is a consultation with the Neuro Wellness clinical team. A psychiatrist reviews your diagnosis, medication history, and overall health, and screens for factors that may affect eligibility — such as bipolar disorder, seizure history, or non-removable metal near the head. Many Fort Lauderdale patients complete the initial consultation by telehealth and travel to Coral Springs for any in-person steps.",
      "If SAINT® is a fit, an fMRI scan is scheduled before the treatment week. The care team coordinates what can be done remotely versus in person, and helps with scheduling, local accommodations, and follow-up that continues close to home in Fort Lauderdale after the treatment week.",
    ],
  },
];

const relatedLinks: RelatedLink[] = [
  {
    title: "SAINT® TMS for Fort Lauderdale Patients",
    href: "/locations/saint-tms-fort-lauderdale",
    description:
      "The 5-day, fMRI-guided SAINT® protocol for patients traveling from Fort Lauderdale",
  },
  {
    title: "What Is SAINT® TMS?",
    href: "/learn/what-is-saint-tms",
    description: "How accelerated, personalized TMS works and who it is for",
  },
  {
    title: "SAINT® vs Traditional TMS",
    href: "/learn/saint-vs-traditional-tms",
    description: "Schedule, targeting, time commitment, and coverage compared",
  },
  {
    title: "Does Insurance Cover SAINT®?",
    href: "/learn/does-insurance-cover-saint-tms",
    description:
      "Coverage, prior authorization, and free benefits verification",
  },
  {
    title: "Check My Insurance",
    href: "/check-insurance-options",
    description: "Free, no-obligation benefits verification",
  },
  {
    title: "Am I a Candidate?",
    href: "/am-i-a-candidate-screener",
    description: "A short self-check — no email required to start",
  },
];

export default function TmsFortLauderdale() {
  return (
    <DepressionContentPage
      seo={{
        title: "TMS Therapy Fort Lauderdale | Neuro Wellness TMS",
        description:
          "TMS therapy for Fort Lauderdale patients at Neuro Wellness in Coral Springs — 30–40 min away. SAINT®, standard TMS, SPRAVATO® & ketamine. Free benefits verification.",
        canonical: "/tms-fort-lauderdale",
        keywords:
          "tms fort lauderdale, tms therapy fort lauderdale, saint tms fort lauderdale, depression treatment fort lauderdale",
        breadcrumbs: [
          { name: "Home", href: "/" },
          { name: "TMS Fort Lauderdale", href: "" },
        ],
      }}
      badge="Serving Fort Lauderdale"
      heroTitle="TMS Therapy for Patients from Fort Lauderdale"
      heroSubtitle="SAINT®, standard TMS, SPRAVATO®, and ketamine — all under one roof in Coral Springs, about 30 to 40 minutes from downtown Fort Lauderdale. Free consultation and benefits verification."
      heroCtaPrimary="Learn If It's a Fit"
      heroCtaPrimaryHref="/am-i-a-candidate-screener"
      sections={sections}
      faq={faqs}
      faqHeading="Fort Lauderdale TMS — Frequently Asked Questions"
      relatedLinks={relatedLinks}
      relatedHeading="Explore Related Resources"
      reviewedBy="Daniel Pietras, MD"
      lastUpdated="2026-11-03"
      finalCtaHeading="See if SAINT® is a fit, free"
      finalCtaText="Talk with our team about your treatment history, your questions, and the options that may be worth discussing. No commitment — just a conversation."
      finalCtaPrimaryLabel="Learn If It's a Fit"
      finalCtaPrimaryHref="/am-i-a-candidate-screener"
      finalCtaSecondaryLabel="Check my insurance"
      finalCtaSecondaryHref="/check-insurance-options"
      footerDisclaimer="SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University."
    />
  );
}
